// src/admin/PrintTranscript.js - In Bảng Điểm Niên Khóa Hiện Tại (Đồng bộ giao diện PrintClassList)
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import classRoomsData from '../data/classRooms.json';
import * as XLSX from 'xlsx';
import './PrintTranscript.css';

const PrintTranscript = () => {
  const {
    currentYearId,
    studentsData,
    classMap,
    catechistsData
  } = useContext(StudentContext);

  const outletContext = useOutletContext();
  const sidebarSelectedClass = outletContext?.selectedClassForOutlet || 'all';

  const [activeClassId, setActiveClassId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [transcripts, setTranscripts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('semester1'); // 'semester1' | 'semester2' | 'yearly'

  // Đồng bộ chọn lớp từ Sidebar hoặc mặc định lớp đầu tiên
  useEffect(() => {
    if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
      setActiveClassId(sidebarSelectedClass);
    } else if (!activeClassId) {
      const firstClassKey = Object.keys(classMap)[0];
      if (firstClassKey) setActiveClassId(firstClassKey);
    }
  }, [sidebarSelectedClass, classMap, activeClassId]);

  // 1. Tải Tên Niên Khóa & Bảng Điểm của Niên Khóa Hiện Tại từ Firestore
  useEffect(() => {
    const fetchCurrentYearTranscripts = async () => {
      if (!currentYearId) return;
      setLoading(true);
      try {
        // Tải thông tin tên niên khóa
        const qYears = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const yearsSnap = await getDocs(qYears);
        const yearsList = yearsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const curYear = yearsList.find(y => y.id === currentYearId || y.isCurrent) || yearsList[0];
        if (curYear) {
          setCurrentYearName(curYear.name || curYear.id);
        }

        // Tải toàn bộ bảng điểm của Niên khóa hiện tại từ subcollection enrollments
        const enrollSnap = await getDocs(
          collection(firestore, `academic_years/${currentYearId}/enrollments`)
        );
        const trMap = {};
        enrollSnap.docs.forEach((d) => {
          const data = d.data();
          const sId = data.studentId || d.id;
          trMap[sId] = data.transcript || {};
        });
        setTranscripts(trMap);
      } catch (error) {
        console.error('Lỗi khi tải bảng điểm niên khóa hiện tại:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentYearTranscripts();
  }, [currentYearId]);

  const currentClassName = classMap[activeClassId] || activeClassId || 'Chưa chọn lớp';

  // Thông tin Phòng và Khối/Ngành từ classRoomsData
  const roomInfo = useMemo(() => {
    if (!activeClassId) return null;
    return classRoomsData.find(
      r => r.classId === activeClassId ||
           (r.className && r.className.trim().toLowerCase() === currentClassName.trim().toLowerCase())
    );
  }, [activeClassId, currentClassName]);

  const roomName = roomInfo?.room || 'Chưa xếp phòng';
  const groupName = roomInfo?.block || 'Chung';

  // Màu sắc banner theo từng ngành
  const groupColorClass = useMemo(() => {
    const norm = (groupName || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (norm.includes('chien')) return 'banner-chien-con';
    if (norm.includes('au')) return 'banner-au';
    if (norm.includes('thieu')) return 'banner-thieu';
    if (norm.includes('nghia')) return 'banner-nghia';
    if (norm.includes('hiep')) return 'banner-hiep-si';
    return 'banner-default';
  }, [groupName]);

  // Danh sách Giáo lý viên đứng lớp
  const assignedTeachers = useMemo(() => {
    if (!currentClassName || !catechistsData) return [];

    const matched = catechistsData.filter(c => {
      if ((c.status || 'active') === 'inactive') return false;
      return c.className === currentClassName || c.className === activeClassId;
    });

    matched.sort((a, b) => {
      const aIsTuSi = (a.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      const bIsTuSi = (b.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      return aIsTuSi - bIsTuSi;
    });

    return matched.map(t => {
      const isTuSi = (t.religious || 'Giáo lý viên') === 'Tu Sĩ';
      const isFemale = (t.gender || 'Nam') === 'Nữ';

      let prefix = isTuSi ? (isFemale ? 'Sr' : 'Thầy') : (isFemale ? 'Chị' : 'Anh');
      const holy = t.holyName ? `${t.holyName} ` : '';
      return `${prefix} ${holy}${t.fullName || ''}`.trim();
    });
  }, [currentClassName, activeClassId, catechistsData]);

  // Tách Tên và Họ đệm
  const splitName = (fullName = '') => {
    const parts = (fullName || '').trim().split(/\s+/);
    const givenName = parts.pop() || '';
    const lastNameAndMiddle = parts.join(' ') || '';
    return { lastNameAndMiddle, givenName };
  };

  // Danh sách thiếu nhi theo lớp đã lọc và sắp xếp theo Tên
  const sortedStudents = useMemo(() => {
    const rawList = (activeClassId === 'all'
      ? Object.values(studentsData || {}).flat()
      : (studentsData[activeClassId] || [])
    );

    const activeStudents = rawList.filter((s) => {
      const st = String(s.status || '').toLowerCase().trim();
      return st !== 'nghỉ luôn' && st !== 'nghỉ học' && st !== 'đã nghỉ';
    });

    return [...activeStudents].sort((a, b) => {
      const nameA = splitName(a.fullName).givenName;
      const nameB = splitName(b.fullName).givenName;
      const cmp = nameA.localeCompare(nameB, 'vi');
      if (cmp !== 0) return cmp;
      return (a.fullName || '').localeCompare(b.fullName || '', 'vi');
    });
  }, [studentsData, activeClassId]);

  const handlePrint = () => {
    window.print();
  };

  // Xuất Excel
  const downloadExcel = () => {
    const semesterText =
      selectedSemester === 'yearly'
        ? 'Cả Năm'
        : selectedSemester === 'semester1'
        ? 'Học kỳ I'
        : 'Học kỳ II';

    const data = [
      [`BẢNG ĐIỂM ${semesterText.toUpperCase()} - LỚP: ${currentClassName.toUpperCase()}`],
      [`Niên khóa: ${currentYearName || 'Niên khóa hiện tại'}`],
      [`Phòng: ${roomName}`],
      []
    ];

    let header = ['STT', 'Mã ID', 'Tên Thánh', 'Họ và Tên Đệm', 'Tên'];
    if (selectedSemester === 'yearly') {
      header.push('TB HK1', 'TB HK2', 'TBCN', 'C. Cần', 'Xếp Hạng');
    } else {
      header.push('Miệng', '15 Phút', '1 Tiết', 'Thi', 'Trung Bình', 'Chuyên Cần');
    }
    data.push(header);

    sortedStudents.forEach((student, index) => {
      const { lastNameAndMiddle, givenName } = splitName(student.fullName);
      const studentTr = transcripts[student.id] || {};
      const score = studentTr[selectedSemester] || {};

      let row = [index + 1, student.id, student.holyName || '', lastNameAndMiddle, givenName];

      if (selectedSemester === 'yearly') {
        row.push(
          studentTr.semester1?.average ?? '',
          studentTr.semester2?.average ?? '',
          score.average ?? '',
          score.diligence ?? '',
          score.rank ?? ''
        );
      } else {
        row.push(
          score.oral ?? '',
          score.fifteenMinutes ?? '',
          score.onePeriod ?? '',
          score.exam ?? '',
          score.average ?? '',
          score.diligence ?? ''
        );
      }
      data.push(row);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'BangDiem');
    XLSX.writeFile(wb, `BangDiem_${currentClassName.replace(/\s+/g, '_')}_${selectedSemester}.xlsx`);
  };

  return (
    <div className="pt-container">
      {/* THANH ĐIỀU KHIỂN TRÊN MÀN HÌNH (ẨN KHI IN) */}
      <div className="pt-toolbar no-print">
        <div className="pt-toolbar-left">
          {/* Lựa chọn lớp (nếu sidebar đang chọn Tất cả các lớp) */}
          {(!sidebarSelectedClass || sidebarSelectedClass === 'all') && (
            <div className="pt-class-select-group">
              <label className="pt-toolbar-label">Chọn lớp:</label>
              <select
                value={activeClassId}
                onChange={(e) => setActiveClassId(e.target.value)}
                className="pt-select-class"
              >
                {Object.entries(classMap).map(([cId, cName]) => (
                  <option key={cId} value={cId}>{cName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Chọn Học kỳ */}
          <div className="pt-semester-tabs">
            <button
              type="button"
              onClick={() => setSelectedSemester('semester1')}
              className={`pt-tab-btn ${selectedSemester === 'semester1' ? 'active' : ''}`}
            >
              Học Kỳ I
            </button>
            <button
              type="button"
              onClick={() => setSelectedSemester('semester2')}
              className={`pt-tab-btn ${selectedSemester === 'semester2' ? 'active' : ''}`}
            >
              Học Kỳ II
            </button>
            <button
              type="button"
              onClick={() => setSelectedSemester('yearly')}
              className={`pt-tab-btn ${selectedSemester === 'yearly' ? 'active' : ''}`}
            >
              Cả Năm
            </button>
          </div>

          {loading && <span className="pt-loading-text">⏳ Đang nạp bảng điểm...</span>}
        </div>

        <div className="pt-toolbar-right">
          <button onClick={downloadExcel} className="pt-btn pt-btn-excel">
            📥 Xuất Excel
          </button>
          <button onClick={handlePrint} className="pt-btn pt-btn-print">
            🖨️ In Bảng Điểm (A4)
          </button>
        </div>
      </div>

      {/* KHUNG TRANG IN CHUẨN A4 ĐỒNG BỘ PRINTCLASSLIST */}
      <div className="pt-sheet">
        {/* PHẦN ĐẦU (GÓC TRÁI GIÁO XỨ & XỨ ĐOÀN) */}
        <div className="pt-header-top">
          <div className="pt-origin-left">
            <div className="pt-parish-name">GIÁO XỨ AN NHƠN</div>
            <div className="pt-group-name">XỨ ĐOÀN PHAOLÔ LÊ VĂN LỘC</div>
            <div className="pt-header-divider"></div>
          </div>
        </div>

        {/* PHẦN TIÊU ĐỀ BANNER THEO MÀU NGÀNH */}
        <section className="pt-title-section">
          <div className={`pt-title-banner ${groupColorClass}`}>
            <h1 className="pt-main-title">
              BẢNG ĐIỂM {selectedSemester === 'yearly' ? 'CẢ NĂM' : (selectedSemester === 'semester1' ? 'HỌC KỲ I' : 'HỌC KỲ II')} - {groupName.toUpperCase().startsWith('NGÀNH') ? groupName.toUpperCase() : `NGÀNH ${groupName.toUpperCase()}`}
            </h1>
            <h2 className="pt-class-title">LỚP {currentClassName.toUpperCase()}</h2>
          </div>
          <p className="pt-year-text">Niên khóa: {currentYearName || 'Niên Khóa Hiện Tại'}</p>
        </section>

        {/* PHẦN PHỤ: PHÒNG & GIÁO LÝ VIÊN ĐỨNG LỚP */}
        <section className="pt-meta-section">
          <div className="pt-meta-item">
            <span className="pt-meta-label">PHÒNG:</span>
            <span className="pt-meta-value">{roomName}</span>
          </div>

          <div className="pt-meta-item pt-meta-teachers">
            <span className="pt-meta-label">ĐỨNG LỚP:</span>
            <div className="pt-teachers-list">
              {assignedTeachers.length > 0 ? (
                assignedTeachers.map((teacher, index) => (
                  <div key={index} className="pt-teacher-line">
                    • {teacher}
                  </div>
                ))
              ) : (
                <div className="pt-teacher-line">— Chưa có phân công</div>
              )}
            </div>
          </div>
        </section>

        {/* BẢNG ĐIỂM CHI TIẾT */}
        <section className="pt-table-section">
          <table className="pt-table">
            <thead>
              <tr>
                <th className="pt-col-stt">STT</th>
                <th className="pt-col-id">Mã ID</th>
                <th className="pt-col-holy">Tên Thánh</th>
                <th className="pt-col-lastname">Họ và Tên Đệm</th>
                <th className="pt-col-name">Tên</th>

                {selectedSemester === 'yearly' ? (
                  <>
                    <th className="pt-col-score">TB HK1</th>
                    <th className="pt-col-score">TB HK2</th>
                    <th className="pt-col-score">TBCN</th>
                    <th className="pt-col-score">C. Cần</th>
                    <th className="pt-col-rank">Xếp Hạng</th>
                  </>
                ) : (
                  <>
                    <th className="pt-col-score">Miệng</th>
                    <th className="pt-col-score">15'</th>
                    <th className="pt-col-score">1 Tiết</th>
                    <th className="pt-col-score">Thi</th>
                    <th className="pt-col-score">TB</th>
                    <th className="pt-col-score">C.Cần</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={selectedSemester === 'yearly' ? 10 : 11} className="pt-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    ⏳ Đang tải dữ liệu điểm...
                  </td>
                </tr>
              ) : sortedStudents.length === 0 ? (
                <tr>
                  <td colSpan={selectedSemester === 'yearly' ? 10 : 11} className="pt-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    Chưa có học sinh nào trong lớp này.
                  </td>
                </tr>
              ) : (
                sortedStudents.map((student, idx) => {
                  const { lastNameAndMiddle, givenName } = splitName(student.fullName);
                  const studentTr = transcripts[student.id] || {};
                  const score = studentTr[selectedSemester] || {};

                  return (
                    <tr key={student.id || idx}>
                      <td className="pt-text-center">{idx + 1}</td>
                      <td className="pt-col-id pt-text-center">{student.id}</td>
                      <td className="pt-col-holy">{student.holyName || '-'}</td>
                      <td className="pt-col-lastname">{lastNameAndMiddle}</td>
                      <td className="pt-col-name pt-font-bold">{givenName}</td>

                      {selectedSemester === 'yearly' ? (
                        <>
                          <td className="pt-text-center">{studentTr.semester1?.average || '-'}</td>
                          <td className="pt-text-center">{studentTr.semester2?.average || '-'}</td>
                          <td className="pt-text-center pt-font-bold">{score.average || '-'}</td>
                          <td className="pt-text-center">{score.diligence || '-'}</td>
                          <td className="pt-text-center pt-font-bold">{score.rank || '-'}</td>
                        </>
                      ) : (
                        <>
                          <td className="pt-text-center">{score.oral || '-'}</td>
                          <td className="pt-text-center">{score.fifteenMinutes || '-'}</td>
                          <td className="pt-text-center">{score.onePeriod || '-'}</td>
                          <td className="pt-text-center">{score.exam || '-'}</td>
                          <td className="pt-text-center pt-font-bold">{score.average || '-'}</td>
                          <td className="pt-text-center">{score.diligence || '-'}</td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default PrintTranscript;